/**
 * Phase 6.3 — Portal "What's new on my portal" badge + drawer.
 *
 * In-app only. No SMS / email / push.
 *
 * Behaviour:
 *   - Hidden when `portal_whats_new_enabled` is OFF or the API returns 404.
 *   - Polls /api/portal/whats-new on mount + every 60s; refreshes on
 *     drawer open and on "Mark all seen".
 *   - Shows a numeric badge (capped at 99+) on the bell icon when
 *     unread_count > 0.
 *   - Drawer lists events newest-first with title, source label, and
 *     relative "x min ago" timestamp. Deep-link target uses portal
 *     react-router hash navigation.
 *   - Provides loading + error + empty states.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription,
} from '@/components/ui/sheet';
import { Bell, Check, FileText, Heart, Target, Award, ChevronRight, RefreshCw } from 'lucide-react';
import api from '@/lib/portalApi';

const POLL_MS = 60_000;

function sourceIcon(sourceType) {
  switch (sourceType) {
    case 'care_plan_section':
    case 'care_plan_structured_row':
      return FileText;
    case 'participant_goal':
      return Target;
    case 'goal_outcome':
      return Award;
    case 'participant_document':
      return FileText;
    default:
      return Heart;
  }
}

function sourceLabel(sourceType) {
  switch (sourceType) {
    case 'care_plan_section': return 'Care plan section';
    case 'care_plan_structured_row': return 'Care plan item';
    case 'participant_goal': return 'Goal';
    case 'goal_outcome': return 'Outcome';
    case 'participant_document': return 'Document';
    case 'portal_comment_approved': return 'Comment';
    case 'ai_plan_manager_summary_published': return 'AI summary';
    default: return 'Update';
  }
}

/**
 * Filter-chip taxonomy. Each chip groups a set of `source_type` values that
 * belong to the same conceptual surface from a participant's perspective.
 *
 * `enabled` controls whether the chip is clickable:
 *   - true  → live, included in `available` filter
 *   - false → rendered greyed-out with a "coming soon" hint, NOT clickable.
 *
 * Comments (Phase 6.2) and AI summary (Phase 6.1) are scaffolded as
 * disabled chips today so the structure is in place; flip `enabled:true`
 * when each phase ships.
 */
const FILTER_CHIPS = [
  { id: 'all',        label: 'All',        sources: null,                                                   enabled: true,  hint: null },
  { id: 'care_plan',  label: 'Care Plan',  sources: ['care_plan_section', 'care_plan_structured_row'],     enabled: true,  hint: null },
  { id: 'goals',      label: 'Goals',      sources: ['participant_goal'],                                   enabled: true,  hint: null },
  { id: 'outcomes',   label: 'Outcomes',   sources: ['goal_outcome'],                                       enabled: true,  hint: null },
  { id: 'documents',  label: 'Documents',  sources: ['participant_document'],                               enabled: true,  hint: null },
  { id: 'comments',   label: 'Comments',   sources: ['portal_comment_approved'],                            enabled: true,  hint: null },
  { id: 'ai_summary', label: 'AI Summary', sources: ['ai_plan_manager_summary_published'],                  enabled: false, hint: 'Phase 6.1 — coming soon' },
];

function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function PortalWhatsNew() {
  const [available, setAvailable] = useState(true);   // false → flag OFF / 404
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [events, setEvents] = useState([]);
  const [unread, setUnread] = useState(0);
  const [headSeq, setHeadSeq] = useState(0);
  const [activeChip, setActiveChip] = useState('all');

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get('/portal/whats-new?limit=50');
      const d = r.data?.data || {};
      setEvents(Array.isArray(d.events) ? d.events : []);
      setUnread(Number(d.unread_count || 0));
      setHeadSeq(Number(d.head_seq || 0));
      setAvailable(true);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        // Flag OFF — silently hide the badge.
        setAvailable(false);
      } else {
        setError(e?.response?.data?.detail || e.message || 'Failed to load updates');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
    const t = setInterval(fetchEvents, POLL_MS);
    return () => clearInterval(t);
  }, [fetchEvents]);

  // Refresh on open.
  useEffect(() => { if (open) fetchEvents(); }, [open, fetchEvents]);

  const markAllSeen = useCallback(async () => {
    try {
      await api.post('/portal/mark-seen', { up_to_seq: headSeq });
      setUnread(0);
    } catch (e) {
      // Swallow; the badge will re-derive from the next poll.
      // eslint-disable-next-line no-console
      console.warn('mark-seen failed', e);
    }
  }, [headSeq]);

  const badgeLabel = useMemo(() => {
    if (!unread) return null;
    return unread > 99 ? '99+' : String(unread);
  }, [unread]);

  /**
   * Filter the loaded event list against the active chip. Filtering is
   * client-side because the badge counter is always the total unread —
   * filters narrow what the user *sees* but never change the
   * compliance/audit semantics of "X unread updates exist".
   */
  const filteredEvents = useMemo(() => {
    const chip = FILTER_CHIPS.find(c => c.id === activeChip) || FILTER_CHIPS[0];
    if (!chip.sources) return events;  // 'all'
    const allowed = new Set(chip.sources);
    return events.filter(ev => allowed.has(ev.source_type));
  }, [events, activeChip]);

  const chipCounts = useMemo(() => {
    const m = new Map();
    for (const ev of events) {
      m.set(ev.source_type, (m.get(ev.source_type) || 0) + 1);
    }
    return FILTER_CHIPS.map(chip => {
      if (!chip.sources) return { ...chip, count: events.length };
      const n = chip.sources.reduce((s, src) => s + (m.get(src) || 0), 0);
      return { ...chip, count: n };
    });
  }, [events]);

  if (!available) return null;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="portal-whats-new-bell"
          aria-label="What's new on my portal"
        >
          <Bell className="h-4 w-4" />
          {badgeLabel && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 min-w-[1.25rem] px-1 text-[10px] font-semibold flex items-center justify-center"
              data-testid="portal-whats-new-badge"
            >
              {badgeLabel}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md" data-testid="portal-whats-new-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Bell className="h-4 w-4" /> What&apos;s new on my portal
          </SheetTitle>
          <SheetDescription>
            Recent updates to the care plan content shared with you.
          </SheetDescription>
        </SheetHeader>

        <div className="flex items-center justify-between mt-4 mb-2">
          <span className="text-xs text-muted-foreground" data-testid="portal-whats-new-unread-count">
            {unread === 0 ? 'All caught up' : `${unread} new`}
          </span>
          <div className="flex gap-1">
            <Button
              variant="ghost" size="sm"
              onClick={fetchEvents}
              disabled={loading}
              data-testid="portal-whats-new-refresh"
            >
              <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button
              variant="outline" size="sm"
              onClick={markAllSeen}
              disabled={unread === 0}
              data-testid="portal-whats-new-mark-seen"
            >
              <Check className="h-3 w-3 mr-1" /> Mark all seen
            </Button>
          </div>
        </div>

        {/* Per-source-type filter chip row. Disabled chips render as
            greyed-out placeholders pre-Phase-6.2 / Phase-6.1 ship. */}
        <div
          className="flex flex-wrap gap-1.5 mb-3 -mx-1 px-1 overflow-x-auto pb-1"
          data-testid="portal-whats-new-filter-chips"
        >
          {chipCounts.map((chip) => {
            const isActive = activeChip === chip.id;
            const base = 'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs border transition-colors whitespace-nowrap';
            const cls = !chip.enabled
              ? `${base} border-dashed text-muted-foreground/60 cursor-not-allowed bg-muted/30`
              : isActive
              ? `${base} bg-primary text-primary-foreground border-primary`
              : `${base} bg-background hover:bg-muted border-border text-foreground`;
            return (
              <button
                key={chip.id}
                type="button"
                className={cls}
                onClick={() => chip.enabled && setActiveChip(chip.id)}
                aria-pressed={isActive}
                aria-disabled={!chip.enabled}
                title={chip.hint || undefined}
                data-testid={`portal-whats-new-chip-${chip.id}`}
              >
                <span>{chip.label}</span>
                {chip.enabled && chip.count > 0 && (
                  <span
                    className={`px-1.5 rounded-full text-[10px] ${isActive ? 'bg-primary-foreground/20' : 'bg-muted-foreground/15'}`}
                    data-testid={`portal-whats-new-chip-count-${chip.id}`}
                  >
                    {chip.count}
                  </span>
                )}
                {!chip.enabled && (
                  <span className="text-[10px] italic opacity-70">soon</span>
                )}
              </button>
            );
          })}
        </div>

        {error && (
          <div
            className="text-xs text-rose-600 border border-rose-200 bg-rose-50 rounded-md p-2 mb-2"
            data-testid="portal-whats-new-error"
          >
            {error}
          </div>
        )}

        {loading && events.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center"
               data-testid="portal-whats-new-loading">
            Loading…
          </div>
        ) : filteredEvents.length === 0 ? (
          <div
            className="text-sm text-muted-foreground py-10 text-center"
            data-testid="portal-whats-new-empty"
          >
            <Bell className="h-6 w-6 mx-auto mb-2 opacity-40" />
            {events.length === 0
              ? 'Nothing new yet. We\u2019ll let you know when your care team publishes an update.'
              : 'No updates in this category yet.'}
          </div>
        ) : (
          <ul className="divide-y border rounded-md overflow-hidden">
            {filteredEvents.map((ev) => {
              const Icon = sourceIcon(ev.source_type);
              return (
                <li key={ev.event_id} data-testid={`portal-whats-new-event-${ev.event_id}`}>
                  <Link
                    to={ev.deep_link_target || '#'}
                    onClick={() => setOpen(false)}
                    className="flex items-start gap-3 p-3 hover:bg-muted/40"
                  >
                    <div className="mt-0.5 h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="h-3.5 w-3.5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{ev.title || 'Update'}</p>
                      <p className="text-xs text-muted-foreground truncate">{ev.safe_summary}</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        <span className="font-medium">{sourceLabel(ev.source_type)}</span>
                        {' · '}{relativeTime(ev.created_at)}
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground mt-2 flex-shrink-0" />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default PortalWhatsNew;
